import React, { useCallback, useState } from 'react';

import ReplayIcon from '@mui/icons-material/Replay';
import { Alert, AlertTitle, Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { INVENTORY_DECISION_LABELS, InventoryDecisionResult, getDecisionSeverity } from '~/types/inventoryWizard';

import InventoryWizard from './InventoryWizard';
import ReopenManagementPane from './ReopenManagementPane';
import StopsellManagementPane from './StopsellManagementPane';

type Mode = 'stopsell' | 'reopen';

interface Props {
  propertyId: string;
  roomTypes: Array<App.RoomType>;
  offers: Array<App.Offer>;
}

export default function AvailabilityManagementPane({ propertyId, roomTypes, offers }: Props) {
  const [mode, setMode] = useState<Mode>('stopsell');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [decisionResult, setDecisionResult] = useState<InventoryDecisionResult | null>(null);

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, value: Mode | null) => {
    if (value) setMode(value);
  };

  const handleOpenWizard = useCallback(() => {
    setDecisionResult(null);
    setMode('stopsell');
    setWizardOpen(true);
  }, []);

  const handleDecision = useCallback((result: InventoryDecisionResult) => {
    setDecisionResult(result);
  }, []);

  return (
    <>
      <PageSubheader title="Availability Management" />

      {!decisionResult && (
        <Box mb={2}>
          <Button variant="contained" onClick={handleOpenWizard}>
            Start Inventory Assessment
          </Button>
        </Box>
      )}

      {decisionResult && (
        <>
          <Box mb={2}>
            <Alert
              severity={getDecisionSeverity(decisionResult.decision)}
              action={
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ReplayIcon fontSize="small" />}
                  onClick={handleOpenWizard}
                  color="inherit"
                >
                  Not the right outcome? Reassess
                </Button>
              }
            >
              <AlertTitle>Assessment complete: {INVENTORY_DECISION_LABELS[decisionResult.decision]}</AlertTitle>
              {decisionResult.reason}
            </Alert>
          </Box>

          {decisionResult.decision === 'UPDATE_INVENTORY' && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                What would you like to do?
              </Typography>
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                <ToggleButton value="stopsell">Stopsell</ToggleButton>
                <ToggleButton value="reopen">Re-open</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </>
      )}

      {decisionResult?.decision === 'UPDATE_INVENTORY' && mode === 'stopsell' && (
        <StopsellManagementPane propertyId={propertyId} roomTypes={roomTypes} hideHeader />
      )}
      {decisionResult?.decision === 'UPDATE_INVENTORY' && mode === 'reopen' && (
        <ReopenManagementPane propertyId={propertyId} roomTypes={roomTypes} hideHeader />
      )}

      <InventoryWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onDecision={handleDecision}
        offers={offers}
      />
    </>
  );
}
